import Phaser from 'phaser';
import { PALETTE } from '../config';
import { AudioEngine } from '../game/AudioEngine';
import { SaveManager } from '../game/SaveManager';
import { CrazyGames } from '../game/crazygames';
import { arenaList, formatTimeLeft, type TournView } from '../game/arena';
import { t } from '../game/i18n';
import { addBackButton } from '../ui/BackButton';
import { Button } from '../ui/Button';
import { drawGradientBackground } from '../ui/Background';
import { drawPanel } from '../ui/Panel';
import { onResize, viewport } from '../ui/viewport';

/** Arena hub: list of joinable / live / mine tournaments. Requires a signed-in account. */
export class Arena extends Phaser.Scene {
  private listObjects: Phaser.GameObjects.GameObject[] = [];
  private tabObjects: Phaser.GameObjects.GameObject[] = [];
  private tab: 'active' | 'past' = 'active';
  private all: TournView[] = [];
  private now = 0;
  private loaded = false;

  constructor() {
    super('Arena');
  }

  create(): void {
    this.build();
    // On CrazyGames the backend account is auto-provisioned in the background; re-render the moment
    // sign-in completes so the gate flips straight to the tournament list (no manual login).
    const onCloud = (): void => { if (SaveManager.instance.signedIn) this.scene.restart(); };
    SaveManager.instance.on('cloudChanged', onCloud);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => SaveManager.instance.off('cloudChanged', onCloud));
    const w0 = this.scale.width, h0 = this.scale.height;
    onResize(this, () => {
      if (Math.abs(this.scale.width - w0) > 2 || Math.abs(this.scale.height - h0) > 2) this.scene.restart();
    });
  }

  private build(): void {
    const { w, h, cx, s } = viewport(this);
    drawGradientBackground(this);
    addBackButton(this, s, () => this.scene.start('MainMenu'));

    this.add.text(cx, h * 0.08, `🏆 ${t('arena.title')}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(28 * s)}px`, color: '#f4f7ff', fontStyle: 'bold',
    }).setOrigin(0.5).setShadow(0, 3, '#000000aa', 6);

    if (!SaveManager.instance.signedIn) { this.gate(w, h, cx, s); return; }

    this.coinChip(w - 16 * s, 12 * s, s);
    new Button(this, cx, h * 0.18, `➕  ${t('arena.create')}`, () => this.scene.start('ArenaCreate'), {
      width: Math.min(300, w * 0.5), height: Math.max(46, 52 * s), color: PALETTE.laneAccent, fontSize: Math.max(15, Math.round(18 * s)),
    });

    const loading = this.add.text(cx, h * 0.55, t('arena.loading'), {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(18 * s)}px`, color: '#9fb0e0',
    }).setOrigin(0.5);

    void arenaList().then((r) => {
      loading.destroy();
      if (!r.ok || !r.data) { this.add.text(cx, h * 0.55, t('arena.offline'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(16 * s)}px`, color: '#ff8f9c' }).setOrigin(0.5); return; }
      SaveManager.instance.setServerCoins(r.data.coins);
      this.all = r.data.tournaments;
      this.now = r.data.now;
      this.loaded = true;
      this.tabs(s);
      this.renderList();
    });
  }

  private isActive(tv: TournView): boolean {
    return tv.status === 'running' || tv.status === 'upcoming';
  }

  /** Two pills: Active (live/upcoming) vs Past (ended/settled), with counts. */
  private tabs(s: number): void {
    const { w, h } = viewport(this);
    this.tabObjects.forEach((o) => o.destroy());
    this.tabObjects = [];
    if (!this.loaded) return;
    const counts = { active: this.all.filter((t2) => this.isActive(t2)).length, past: this.all.length - this.all.filter((t2) => this.isActive(t2)).length };
    const defs: { key: 'active' | 'past'; label: string }[] = [
      { key: 'active', label: `${t('arena.tabActive')} (${counts.active})` },
      { key: 'past', label: `${t('arena.tabPast')} (${counts.past})` },
    ];
    const y = h * 0.265, hh = Math.max(32, 36 * s), gap = 10 * s;
    const probe = defs.map((d) => { const tx = this.add.text(-999, -999, d.label, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(13, Math.round(15 * s))}px`, fontStyle: 'bold' }); const wd = tx.width + 28 * s; tx.destroy(); return { ...d, wd }; });
    const total = probe.reduce((a, b) => a + b.wd, 0) + gap;
    let x = w / 2 - total / 2;
    for (const p of probe) {
      const active = this.tab === p.key;
      const g = this.add.graphics();
      g.fillStyle(active ? PALETTE.laneAccent : PALETTE.ink, active ? 0.95 : 0.5).fillRoundedRect(x, y - hh / 2, p.wd, hh, hh / 2);
      g.lineStyle(1.5, active ? PALETTE.white : PALETTE.cadence, active ? 0.6 : 0.3).strokeRoundedRect(x, y - hh / 2, p.wd, hh, hh / 2);
      const tx = this.add.text(x + p.wd / 2, y, p.label, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(13, Math.round(15 * s))}px`, color: active ? '#0b1020' : '#cfd8f5', fontStyle: 'bold' }).setOrigin(0.5);
      this.tabObjects.push(g, tx);
      if (!active) g.setInteractive(new Phaser.Geom.Rectangle(x, y - hh / 2, p.wd, hh), Phaser.Geom.Rectangle.Contains).on(Phaser.Input.Events.POINTER_DOWN, () => { AudioEngine.tap(0.3); this.tab = p.key; this.tabs(s); this.renderList(); });
      x += p.wd + gap;
    }
  }

  private gate(w: number, h: number, cx: number, s: number): void {
    drawPanel(this, cx, h * 0.52, Math.min(520, w * 0.82), Math.min(h * 0.42, 240 * s), { radius: 18, fillAlpha: 0.66 });
    // On CrazyGames the account is being auto-provisioned in the background — just show a spinner-ish
    // message; the create() listener restarts this scene the instant sign-in lands.
    if (CrazyGames.hasData) {
      const loading = this.add.text(cx, h * 0.52, t('arena.loading'), {
        fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(18 * s)}px`, color: '#9fb0e0',
      }).setOrigin(0.5);
      // If the auto sign-in can't complete (e.g. the portal blocks our API), don't spin forever.
      this.time.delayedCall(6000, () => { if (!SaveManager.instance.signedIn) loading.setText(t('arena.offline')); });
      return;
    }
    this.add.text(cx, h * 0.46, t('arena.gate'), {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(15, Math.round(17 * s))}px`, color: '#cfd8f5',
      align: 'center', wordWrap: { width: Math.min(480, w * 0.74) }, lineSpacing: 5,
    }).setOrigin(0.5);
    new Button(this, cx, h * 0.62, t('arena.gateBtn'), () => this.scene.start('Account'), {
      width: Math.min(300, w * 0.6), height: Math.max(52, 58 * s), color: PALETTE.cadence, fontSize: Math.max(15, Math.round(18 * s)),
    });
  }

  private renderList(): void {
    const { w, h, cx, s } = viewport(this);
    const now = this.now;
    this.listObjects.forEach((o) => o.destroy());
    this.listObjects = [];

    const tournaments = this.all
      .filter((tv) => (this.tab === 'active' ? this.isActive(tv) : !this.isActive(tv)))
      .sort((a, b) => (this.tab === 'active'
        ? (a.status === 'running' ? 0 : 1) - (b.status === 'running' ? 0 : 1) || a.endAt - b.endAt
        : b.endAt - a.endAt));

    if (!tournaments.length) {
      const msg = this.tab === 'active' ? t('arena.empty') : t('arena.emptyPast');
      this.listObjects.push(this.add.text(cx, h * 0.58, msg, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(16 * s)}px`, color: '#9fb0e0', align: 'center', wordWrap: { width: w * 0.7 } }).setOrigin(0.5));
      return;
    }

    const top = h * 0.32, bottom = h - 18 * s;
    const cardH = Math.max(78, 92 * s), gap = 10 * s;
    const cardW = Math.min(640, w * 0.9);
    const x = cx - cardW / 2;

    // Masked, drag-scrollable container holding the cards.
    const container = this.add.container(0, top);
    const maskG = this.make.graphics({}).fillRect(x, top, cardW, bottom - top);
    container.setMask(maskG.createGeometryMask());
    this.listObjects.push(container, maskG);

    tournaments.forEach((tv, i) => this.card(container, tv, x, i * (cardH + gap), cardW, cardH, s, now));

    const totalH = tournaments.length * (cardH + gap);
    const viewH = bottom - top;
    if (totalH > viewH) this.enableScroll(container, top, x, cardW, viewH, totalH);
  }

  private enableScroll(container: Phaser.GameObjects.Container, top: number, x: number, w: number, viewH: number, totalH: number): void {
    const minY = top - (totalH - viewH);
    const maxY = top;
    let dragging = false, lastY = 0;
    const zone = this.add.zone(x, top, w, viewH).setOrigin(0, 0).setInteractive();
    this.listObjects.push(zone);
    zone.on('pointerdown', (p: Phaser.Input.Pointer) => { dragging = true; lastY = p.y; });
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!dragging || !p.isDown) return;
      container.y = Phaser.Math.Clamp(container.y + (p.y - lastY), minY, maxY);
      lastY = p.y;
    });
    this.input.on('pointerup', () => { dragging = false; });
    this.input.on('wheel', (_p: Phaser.Input.Pointer, _o: unknown, _dx: number, dy: number) => {
      container.y = Phaser.Math.Clamp(container.y - dy * 0.5, minY, maxY);
    });
  }

  private card(parent: Phaser.GameObjects.Container, tv: TournView, x: number, y: number, cw: number, ch: number, s: number, now: number): void {
    const g = this.add.graphics();
    g.fillStyle(0x000000, 0.25).fillRoundedRect(x, y + 3, cw, ch, 14);
    g.fillStyle(PALETTE.bgTop, 0.6).fillRoundedRect(x, y, cw, ch, 14);
    g.lineStyle(2, tv.joined ? PALETTE.good : PALETTE.cadence, tv.joined ? 0.8 : 0.3).strokeRoundedRect(x, y, cw, ch, 14);
    parent.add(g);

    const pad = 16 * s;
    // Name + system/private badge.
    const icon = tv.system ? '⭐ ' : (tv.visibility === 'private' ? '🔒 ' : '');
    const name = this.add.text(x + pad, y + ch * 0.26, `${icon}${tv.name}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(16, Math.round(19 * s))}px`, color: '#f4f7ff', fontStyle: 'bold',
    }).setOrigin(0, 0.5);
    const maxNameW = cw * 0.56;
    if (name.width > maxNameW) name.setScale(maxNameW / name.width);
    parent.add(name);

    // Sub-line: disciplines + entrants.
    parent.add(this.add.text(x + pad, y + ch * 0.62, `${tv.disciplines.length} ${t('arena.disciplines').toLowerCase()} · ${tv.entrantCount} ${t('arena.entrants')}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(12, Math.round(13 * s))}px`, color: '#9fb0e0',
    }).setOrigin(0, 0.5));

    // Status badge (top-right).
    const st = tv.status === 'running' ? { txt: t('arena.statusRunning'), c: PALETTE.good }
      : tv.status === 'upcoming' ? { txt: t('arena.statusUpcoming'), c: PALETTE.cadence }
      : { txt: t('arena.statusEnded'), c: PALETTE.trackLine };
    const badge = this.add.text(x + cw - pad, y + ch * 0.24, tv.joined ? `✓ ${t('arena.joined')}` : st.txt, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(12, Math.round(13 * s))}px`,
      color: tv.joined ? '#6ef0a0' : '#cfd8f5', fontStyle: 'bold',
    }).setOrigin(1, 0.5);
    parent.add(badge);

    // Pool + fee + time on the right.
    const fee = tv.entryFee > 0 ? `${tv.entryFee} 🪙` : t('arena.free');
    parent.add(this.add.text(x + cw - pad, y + ch * 0.56, `${t('arena.pool')}: ${tv.pool} 🪙`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(12, Math.round(14 * s))}px`, color: '#ffd24a', fontStyle: 'bold',
    }).setOrigin(1, 0.5));
    const timeStr = tv.status === 'upcoming' ? formatTimeLeft(tv.startAt - now) : formatTimeLeft(tv.endAt - now);
    parent.add(this.add.text(x + cw - pad, y + ch * 0.8, `${t('arena.fee')} ${fee} · ⏱ ${tv.status === 'settled' ? t('arena.statusEnded') : timeStr}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(11, Math.round(12 * s))}px`, color: '#8893b8',
    }).setOrigin(1, 0.5));

    g.setInteractive(new Phaser.Geom.Rectangle(x, y, cw, ch), Phaser.Geom.Rectangle.Contains)
      .on(Phaser.Input.Events.POINTER_UP, () => { AudioEngine.tap(0.4); this.scene.start('ArenaDetail', { tournamentId: tv.id }); });
  }

  private coinChip(rightX: number, y: number, s: number): void {
    const fs = Math.round(Math.max(17, 19 * s));
    const tx = this.add.text(0, 0, `🪙 ${SaveManager.instance.coins}`, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fs}px`, color: '#ffffff', fontStyle: 'bold' }).setShadow(0, 2, '#000000cc', 3);
    const padX = 14 * s, ww = tx.width + padX * 2, hh = tx.height + 12 * s, bx = rightX - ww;
    this.add.graphics().setDepth(18)
      .fillStyle(PALETTE.ink, 0.78).fillRoundedRect(bx, y, ww, hh, hh / 2)
      .lineStyle(2, PALETTE.cadence, 0.6).strokeRoundedRect(bx, y, ww, hh, hh / 2);
    tx.setPosition(bx + padX, y + hh / 2).setOrigin(0, 0.5).setDepth(19);
  }
}
